export type SearchIndicatorState =
  | { phase: "idle" }
  | { phase: "waiting" | "debouncing" | "working"; label: string }
  | { phase: "downloading"; progress: number; loaded?: number; total?: number }
  | {
      phase: "no-results" | "inference-error" | "load-error";
      title: string;
      detail: string;
      help: string;
    };

const RING_RADIUS = 9.5;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

export function SearchStatusIndicator({ status }: { status: SearchIndicatorState }) {
  if (status.phase === "no-results" || status.phase === "inference-error" || status.phase === "load-error") {
    const isLoadError = status.phase === "load-error";
    return (
      <details className={`search-status-indicator issue ${status.phase}`}>
        <summary aria-label={`${status.title}. Select for details.`}>
          <span className="search-status-circle" aria-hidden="true" />
        </summary>
        <div className="search-status-popover" role="alert">
          <strong>{status.title}</strong>
          <p>{status.detail}</p>
          <small>{status.help}</small>
        </div>
        <span className="visually-hidden" role="status" aria-live="assertive">
          {isLoadError ? "Smart search loading error." : "Smart search did not complete successfully."}
        </span>
      </details>
    );
  }

  const isDownloading = status.phase === "downloading";
  const progress = isDownloading ? Math.round(Math.min(100, Math.max(0, status.progress))) : 100;
  const byteProgress = isDownloading && status.loaded !== undefined && status.total
    ? `, ${formatBytes(status.loaded)} of ${formatBytes(status.total)}`
    : "";
  const valueText = `${progress}%${byteProgress}`;
  const label = status.phase === "waiting" || status.phase === "debouncing" || status.phase === "working"
    ? status.label
    : undefined;
  const downloadOffset = Number((RING_CIRCUMFERENCE * (1 - progress / 100)).toFixed(3));

  return (
    <span
      className={`search-status-indicator activity ${status.phase}`}
      role={isDownloading ? "progressbar" : status.phase === "idle" ? undefined : "status"}
      aria-live={label ? "polite" : undefined}
      aria-label={isDownloading ? "Downloading smart search model" : undefined}
      aria-valuemin={isDownloading ? 0 : undefined}
      aria-valuemax={isDownloading ? 100 : undefined}
      aria-valuenow={isDownloading ? progress : undefined}
      aria-valuetext={isDownloading ? valueText : undefined}
      title={isDownloading ? `Downloading smart search model: ${valueText}` : undefined}
    >
      <span className="search-status-glyph" aria-hidden="true">
        <svg viewBox="0 0 24 24">
          <circle cx="10" cy="10" r="6" />
          <path d="m14.5 14.5 5 5" />
        </svg>
      </span>
      <span className="search-status-ring-wrap" aria-hidden="true">
        <svg className="search-status-ring" viewBox="0 0 24 24">
          <circle className="search-status-ring-track" cx="12" cy="12" r={RING_RADIUS} />
          <circle
            className="search-status-ring-value"
            cx="12"
            cy="12"
            r={RING_RADIUS}
            strokeDasharray={RING_CIRCUMFERENCE}
            strokeDashoffset={isDownloading ? downloadOffset : 0}
          />
        </svg>
      </span>
      {label && <span className="visually-hidden">{label}</span>}
    </span>
  );
}

function formatBytes(bytes: number) {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
